import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';
import store from '@/modules/MuiDialog/store';
import { generateUUIDv4 } from '@/modules/MuiDialog/utils/uuid';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    PaperProps,
} from '@mui/material';
import { LoadingButton } from './components/LoadingButton';
import DialogTitleBox from './DialogTitleBox';
// @ts-ignore - react-draggable may not be installed
let Draggable: any;
try { Draggable = require('react-draggable').default; } catch { Draggable = null; }

const BasicDialog = ({ id = '', title = '', message = '', status = true, isAllowHtml = false, ...props }) => {
    const [isModalOpen, setIsModalOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isWaitClose, setIsWaitClose] = useState(false);
    const [isWaitConfirm, setIsWaitConfirm] = useState(false);
    const [actionStatus, setActionStatus] = useState({});

    const { removeModal, dialogConfig } = useStore(store);

    const {
        onClose = () => {},
        onExited,
        onConfirmed,
        actions,
        component,
        confirmText,
        closeText,
        innerElement,
        ButtonProps,
        closeButtonProps,
        confirmButtonProps,
        persistent = false,
        isDraggable = false,
        disableBackdropClick = false,
        accentColor,
        ...modalProps
    } = props;
    const initButtonProps = ButtonProps || {};

    useEffect(() => {
        setIsModalOpen(status);
    }, [status]);

    useEffect(() => {
        const isActionLoaded = Object.values(actionStatus).findIndex((o) => !!o) >= 0;

        setIsLoading(isWaitConfirm || isWaitClose || isActionLoaded);
    }, [isWaitConfirm, isWaitClose, actionStatus]);

    const getActionLoading = (id: string) => {
        const status: Record<string, any> = actionStatus;
        return status[id] ? status[id] : false;
    };
    const setActionLoading = (id: string, value: boolean) => {
        const status: Record<string, any> = { ...actionStatus };

        status[id] = value;
        setActionStatus(status);
    };

    const computedActions = useMemo(() => {
        if (!actions) return [];

        let nActions = [...actions];
        nActions.forEach((item) => {
            item.id = generateUUIDv4();
        });
        return nActions;
    }, [actions]);

    const computedProps = useMemo(() => {
        // return Object.assign({}, dialogConfig, modalProps);
        return modalProps;
    }, [modalProps, dialogConfig]);

    const handleExit = async (ev?: any) => {
        try {
            if (!onExited) return ev;

            return await onExited();
        } catch (e) {
            throw e;
        } finally {
            removeModal(id);
        }
    };

    const handleConfirm = async (ev?: any) => {
        setIsWaitConfirm(true);
        try {
            let result;
            if (onConfirmed) {
                result = await onConfirmed();
            }

            setIsModalOpen(false);
            return onConfirmed ? result : false;
        } catch (e) {
            throw e;
        } finally {
            setIsWaitConfirm(false);
        }
    };

    const handleClose = async (ev?: any, reason?: string) => {
        setIsWaitClose(true);
        try {
            if (!!disableBackdropClick) return;
            let result;
            if (onClose) {
                result = await onClose();
            }

            setIsModalOpen(false);
            return onClose ? result : false;
        } catch (e) {
            throw e;
        } finally {
            setIsWaitClose(false);
        }
    };

    function PaperComponent(props: PaperProps) {
        return (
            <Draggable handle={`#${id}-draggable-title`} cancel={'[class*="MuiDialogContent-root"]'}>
                <Paper {...props} />
            </Draggable>
        );
    }

    const TransitionProps = Object.assign({}, modalProps?.TransitionProps || {}, {
        onExited: handleExit,
    });

    const actionType =
        (computedActions?.length ? 'custom' : undefined) || (onConfirmed ? 'confirm' : undefined) || 'alert';
    const AlertAction = () => (
        <LoadingButton
            variant="outlined"
            onClick={handleClose}
            isLoading={isWaitClose}
            disabled={isLoading}
            {...Object.assign({}, initButtonProps, closeButtonProps || {})}
        >
            {closeText || 'dismiss'}
        </LoadingButton>
    );
    const accentButtonSx = accentColor ? {
        bgcolor: accentColor,
        '&:hover': { bgcolor: accentColor, filter: 'brightness(0.85)' },
    } : {};
    const accentOutlinedSx = accentColor ? {
        color: `${accentColor} !important`,
        borderColor: accentColor,
        '&:hover': { borderColor: accentColor, bgcolor: `${accentColor}08` },
    } : {};
    const ConfirmAction = () => (
        <>
            <LoadingButton
                variant="contained"
                onClick={handleConfirm}
                isLoading={isWaitConfirm}
                disabled={isLoading}
                {...Object.assign({}, initButtonProps, confirmButtonProps || {})}
                sx={{ ...accentButtonSx, ...(confirmButtonProps?.sx as object || {}) }}
            >
                {confirmText || 'submit'}
            </LoadingButton>
            <LoadingButton
                variant="outlined"
                onClick={handleClose}
                isLoading={isWaitClose}
                disabled={isLoading}
                {...Object.assign({}, initButtonProps, closeButtonProps || {})}
                sx={{ ...accentOutlinedSx, ...(closeButtonProps?.sx as object || {}) }}
            >
                {closeText || 'dismiss'}
            </LoadingButton>
        </>
    );
    const CustomAction = () => (
        <>
            {computedActions.map((action: any, idx: number) => {
                const { buttonProps } = action;
                return (
                    <LoadingButton
                        variant="outlined"
                        key={action?.id || idx}
                        isLoading={getActionLoading(action?.id || idx)}
                        disabled={isLoading}
                        onClick={async () => {
                            setActionLoading(action?.id || idx, true);
                            try {
                                const result = await action.action();
                                if (!action?.isActiveAfterAction) setIsModalOpen(false);

                                return result;
                            } catch (e) {
                                console.error(e);
                            } finally {
                                setActionLoading(action?.id || idx, false);
                            }
                        }}
                        {...Object.assign({}, initButtonProps, action?.buttonProps || {})}
                    >
                        {action?.label}
                    </LoadingButton>
                );
            })}
        </>
    );

    // useEffect(() => {
    //     console.log('changed', isModalOpen);
    // }, [isModalOpen])

    return (
        <Dialog
            open={isModalOpen}
            onClose={!persistent ? handleClose : onClose}
            TransitionProps={TransitionProps}
            PaperComponent={!!isDraggable ? PaperComponent : undefined}
            aria-labelledby={`${id}-draggable-title`}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    overflow: 'hidden',
                },
            }}
            {...computedProps}
        >
            {innerElement ? (
                innerElement
            ) : (
                <>
                    <DialogTitleBox title={title} dialogId={id || ''} isDraggable={isDraggable} accentColor={accentColor} />
                    <DialogContent>
                        <DialogContentText>
                            {isAllowHtml ? <div dangerouslySetInnerHTML={{ __html: message }} /> : message}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        {
                            {
                                custom: <CustomAction />,
                                confirm: <ConfirmAction />,
                                alert: <AlertAction />,
                            }[actionType]
                        }
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default BasicDialog;
